/**
 * Retry helper for burst fan-out fetches against rate-limited services.
 *
 * The transcript microservice sits behind a Cloud Armor rate limiter. When the
 * Transcripts page fans out many paginated requests (e.g. loading every page of
 * a large partner's transcripts to filter by review status client-side), the
 * burst can trip that limiter and come back as HTTP 429. Those 429 responses are
 * emitted by the load balancer without CORS headers, so the browser rejects the
 * fetch as a network/CORS error — which the ApiClient surfaces as ApiError with
 * status 0. Both cases are transient and the requests are idempotent GETs, so
 * retrying with backoff recovers instead of failing the whole operation.
 *
 */

/** HTTP status the LB uses when it rate-limits a burst. */
const TOO_MANY_REQUESTS = 429;
/** ApiClient maps network/CORS failures (incl. header-less 429s) to status 0. */
const NETWORK_OR_CORS = 0;

export interface RetryOptions {
  /** Attempts after the first try (default 4). */
  retries?: number;
  /** Base backoff in ms; doubles each attempt (default 500). */
  baseDelayMs?: number;
  /** Ceiling for a single backoff wait in ms (default 8000). */
  maxDelayMs?: number;
  /** Decide whether an error is worth retrying (default: 429 / network / CORS). */
  shouldRetry?: (err: unknown) => boolean;
  /** Injectable sleep so tests don't wait on real timers. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * True for a transient rate-limit (429) or network/CORS (status 0) failure —
 * the errors worth retrying. Any other error is left for the caller to handle.
 * Detected via a numeric `status` field (what ApiError carries) so no import of
 * the API client is needed.
 */
export function isRateLimitError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const status = (err as { status?: unknown }).status;
  return status === TOO_MANY_REQUESTS || status === NETWORK_OR_CORS;
}

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run `fn`, retrying transient rate-limit/network failures with exponential
 * backoff plus jitter. Resolves with the first success; rejects with the last
 * error once retries are exhausted (or immediately for non-retryable errors).
 */
export async function withRetry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const {
    retries = 4,
    baseDelayMs = 500,
    maxDelayMs = 8000,
    shouldRetry = isRateLimitError,
    sleep = defaultSleep,
  } = options;

  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= retries || !shouldRetry(err)) throw err;
      const backoff = Math.min(maxDelayMs, baseDelayMs * 2 ** attempt);
      // Full jitter: spread concurrent retries so a batch doesn't re-burst in lockstep.
      const delay = Math.random() * backoff;
      await sleep(delay);
      attempt += 1;
    }
  }
}
